import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FadeAndScaleIn } from '@animations/FadeAndScaleIn';
import EmblaCarousel from 'embla-carousel';

@Component({
    selector: 'datepicker',
    templateUrl: 'datepicker.component.html',
    styleUrls: ['datepicker.component.scss'],
    animations: [FadeAndScaleIn],
    standalone: false
})
export class DatepickerComponent implements OnInit {
  @ViewChild('root', { static: false }) root: any;
  @Input('date') dateInput: any;
  @Input('startDate') startDateInput: any;
  @Input('endDate') endDateInput: any;
  @Input('defaultDate') defaultDate = new Date();
  @Input('shown') shown:boolean = false;
  @Input('singleDate') singleDate:boolean = false;
  @Input('calendarView') calendarView:'years'|'months'|'days' = 'days';
  @Input('minDate') minDate: any;
  @Input('maxDate') maxDate: any;
  @Output('pick') pickEventEmitter:EventEmitter<any> = new EventEmitter();
  @Input('time') time:boolean = false;
  @ViewChild('hourRoller', { static: false }) hourRoller: any;
  @ViewChild('minuteRoller', { static: false }) minuteRoller: any;
  @ViewChild('periodRoller', { static: false }) periodRoller: any;
  date: any;
  startDate: any;
  endDate: any;
  targetDate:'startDate'|'endDate' = 'startDate';
  yearSetSize:number = 30;
  hours = Array(12).fill(null).map((UNUSED, index) => {
    const hour = (index + 11) % 12 + 1;
    return ('00' + hour).substr(-2, 2);
  });
  minutes = Array(60).fill(null).map((UNUSED, index) => {
    const minute = index;
    return ('00' + minute).substr(-2, 2)
  });
  periods = ['AM', 'PM'];
  hourRollerEmbla: any;
  minuteRollerEmbla: any;
  periodRollerEmbla: any;

  /**
   * Desktop shows two months at a time. In mobile, it's only one month
   * at a time, and that is achieved using CSS. We will need to keep
   * track of which of the two months to show.
   */
  mobileCalendarView:0|1 = 0; // 0 = viewing first month, 1 = viewing second month
  targetDateObj:any;
  yearsData:any = [/*
    [
      2011, 2012, 2013, 2014, 2015,
      ...
      2036, 2037, 2038, 2039, 2040
    ],
    [
      2041, 2042, 2043, 2044, 2045,
      ...
      2066, 2067, 2068, 2069, 2070
    ]
  */];
  monthsData:any = [/*
    {
      year: 2020,
      months: [1, 2, 3, 4... 12]
    },
    {
      year: 2021,
      months: [1, 2, 3, 4... 12]
    }
   */];
  daysData:any = [/*
    {
      month: 11,
      year: 2020,
      days: {
        previousMonth: [28, 29, 30, 31],
        thisMonth: [1, 2, 3, 4, 5, 6, 7, 8, 9, ... 28, 29, 30],
        nextMonth: [1, 2]
      }
    },
    {
      month: 12,
      year: 2020,
      days: {
        previousMonth: [29, 30],
        thisMonth: [1, 2, 3, 4, 5, 6, 7, 8, 9, ... 28, 29, 31],
        nextMonth: [1, 2, 3, 4, 5, 6]
      }
    }
   */];

  singleDateFieldContext = {
    label: 'Date',
    targetDate: () => this.date,
    highlighted: () => false,
    clickable: false
  };

  startDateFieldContext = {
    label: 'Start',
    targetDate: () => this.startDate,
    highlighted: () => this.targetDate === 'startDate',
    clickable: true
  };

  endDateFieldContext = {
    label: 'End',
    targetDate: () => this.endDate,
    highlighted: () => this.targetDate === 'endDate',
    clickable: true
  };

  constructor() { }

  ngOnInit():void {
    this.reset();
  }

  handleAnimationStart() {
    if(this.time && this.shown) {
      this.hourRollerEmbla = this.EmblaWheel({
        holder: this.hourRoller.nativeElement,
        options: {
          axis: 'y',
          loop: true,
          startIndex: this.date.getHours() - (this.date.getHours() > 12 ? 12 : 0)
        }
      });

      this.minuteRollerEmbla = this.EmblaWheel({
        holder: this.minuteRoller.nativeElement,
        options: {
          axis: 'y',
          loop: true,
          startIndex: this.date.getMinutes()
        }
      });

      this.periodRollerEmbla = this.EmblaWheel({
        holder: this.periodRoller.nativeElement,
        options: {
          axis: 'y',
          startIndex: (this.date.getHours() > 12) ? 1 : 0
        }
      });
    }
  }

  handleAnimationDone() {
    if(!this.shown) {
      if(this.hourRollerEmbla) {
        this.hourRollerEmbla.destroy();
        this.hourRollerEmbla = undefined;
      }

      if(this.minuteRollerEmbla) {
        this.minuteRollerEmbla.destroy();
        this.minuteRollerEmbla = undefined;
      }

      if(this.periodRollerEmbla) {
        this.periodRollerEmbla.destroy();
        this.periodRollerEmbla = undefined;
      }
    }
  }

  reset():void {
    if(this.startDateInput && this.endDateInput) {
      this.startDate = new Date(this.startDateInput);
      this.endDate = new Date(this.endDateInput);
      this.targetDate = 'startDate';
      this.targetDateObj = new Date(this.startDate);
    } else if(this.dateInput) {
      this.date = new Date(this.dateInput);
      this.targetDateObj = new Date(this.date);
    } else {
      this.date = new Date(this.defaultDate);
      this.targetDateObj = new Date(this.defaultDate);
    }

    this.makeCalendarData();
  }

  show():void {
    this.reset();
    this.shown = true;
  }

  hide():void {
    this.shown = false;
  }

  changeTargetDate(targetDate:'startDate'|'endDate'):void {
    let _targetDate = (targetDate === 'startDate')
                    ? new Date(this.startDate)
                    : new Date(this.endDate);
    let currentStartDate = new Date();
    let currentEndDate = new Date();

    this.resetDateTime(_targetDate);
    _targetDate.setDate(1);

    this.resetDateTime(currentStartDate);
    currentStartDate.setFullYear(this.daysData[0].year);
    currentStartDate.setMonth(this.daysData[0].month);
    currentStartDate.setDate(this.daysData[0].days.thisMonth[0]);

    this.resetDateTime(currentEndDate);
    currentEndDate.setFullYear(this.daysData[1].year);
    currentEndDate.setMonth(this.daysData[1].month);
    currentEndDate.setDate(this.daysData[1].days.thisMonth[this.daysData[1].days.thisMonth.length - 1]);

    if(_targetDate < currentStartDate) {
      this.targetDateObj = new Date(_targetDate);
      this.previousMobileCalendarView();
      this.makeCalendarData();
    } else if(_targetDate > currentEndDate) {
      _targetDate.setMonth(_targetDate.getMonth() - 1);
      this.nextMobileCalendarView();
      this.targetDateObj = new Date(_targetDate);
      this.makeCalendarData();
    } else if(_targetDate.getMonth() === this.daysData[0].month) {
      this.previousMobileCalendarView();
    } else {
      this.nextMobileCalendarView();
    }

    this.targetDate = targetDate;
  }

  previousMonth():void {
    this.targetDateObj = new Date(this.targetDateObj.setMonth(this.targetDateObj.getMonth() - 1));
    this.makeCalendarData();
  }

  nextMonth():void {
    this.targetDateObj = new Date(this.targetDateObj.setMonth(this.targetDateObj.getMonth() + 1));
    this.makeCalendarData();
  }

  previousYear():void {
    this.targetDateObj = new Date(this.targetDateObj.setFullYear(this.targetDateObj.getFullYear() - 1));
    this.makeCalendarData();
  }

  nextYear():void {
    this.targetDateObj = new Date(this.targetDateObj.setFullYear(this.targetDateObj.getFullYear() + 1));
    this.makeCalendarData();
  }

  previousYearSet():void {
    this.targetDateObj = new Date(this.targetDateObj.setFullYear(this.targetDateObj.getFullYear() - this.yearSetSize));
    this.makeCalendarData();
  }

  nextYearSet():void {
    this.targetDateObj = new Date(this.targetDateObj.setFullYear(this.targetDateObj.getFullYear() + this.yearSetSize));
    this.makeCalendarData();
  }

  previousMobileCalendarView():void {
    this.mobileCalendarView = 0;
  }

  nextMobileCalendarView():void {
    this.mobileCalendarView = 1;
  }

  makeCalendarData() {
    let _targetDate = new Date(this.targetDateObj);
    let _nextMonthDate;

    this.resetDateTime(_targetDate);
    _targetDate.setDate(1);

    _nextMonthDate = new Date(_targetDate);
    _nextMonthDate.setMonth(_targetDate.getMonth() + 1);

    this.yearsData = this.makeYearsDataFromDate(_targetDate);

    this.monthsData = this.makeMonthsDataFromDate(_targetDate);

    if (this.singleDate) {
      this.daysData = [
        this.makeSingleMonthDaysDataFromDate(_targetDate),
      ];
    }
    else {
      this.daysData = [
        this.makeSingleMonthDaysDataFromDate(_targetDate),
        this.makeSingleMonthDaysDataFromDate(_nextMonthDate)
      ];
    }
  }

  makeYearsDataFromDate(targetDate: any) {
    let targetYear = targetDate.getFullYear();
    let remainder = targetYear % this.yearSetSize;
    let startingYear = targetYear - remainder + 1;
    let endingYear = startingYear + this.yearSetSize;
    let firstYearGroup = [];
    let secondYearGroup = [];

    for(let i = startingYear; i < endingYear; i++) {
      firstYearGroup.push(i);
    }

    for(let i = startingYear + this.yearSetSize; i < endingYear + this.yearSetSize; i++) {
      secondYearGroup.push(i);
    }

    return [
      firstYearGroup,
      secondYearGroup
    ];
  }

  makeMonthsDataFromDate(targetDate: any) {
    return [
      {
        year: targetDate.getFullYear(),
        months: [
          { value: 0,  label: 'January'   },
          { value: 1,  label: 'February'  },
          { value: 2,  label: 'March'     },
          { value: 3,  label: 'April'     },
          { value: 4,  label: 'May'       },
          { value: 5,  label: 'June'      },
          { value: 6,  label: 'July'      },
          { value: 7,  label: 'August'    },
          { value: 8,  label: 'September' },
          { value: 9,  label: 'October'   },
          { value: 10, label: 'November'  },
          { value: 11, label: 'December'  },
        ]
      },
      {
        year: targetDate.getFullYear() + 1,
        months: [
          { value: 0,  label: 'January'   },
          { value: 1,  label: 'February'  },
          { value: 2,  label: 'March'     },
          { value: 3,  label: 'April'     },
          { value: 4,  label: 'May'       },
          { value: 5,  label: 'June'      },
          { value: 6,  label: 'July'      },
          { value: 7,  label: 'August'    },
          { value: 8,  label: 'September' },
          { value: 9,  label: 'October'   },
          { value: 10, label: 'November'  },
          { value: 11, label: 'December'  },
        ]
      }
    ];
  }

  makeSingleMonthDaysDataFromDate(targetDate: any) {
    const date = new Date(targetDate);
    const targetMonth = date.getMonth();
    const previousMonthDays = [];
    const thisMonthDays = [];
    const nextMonthDays = [];

    date.setDate(1);

    while(date.getMonth() === targetMonth) {
      thisMonthDays.push(date.getDate());
      date.setDate(date.getDate() + 1);
    }

    while(date.getDay() !== 1) {
      nextMonthDays.push(date.getDate());
      date.setDate(date.getDate() + 1);
    }

    date.setTime(targetDate);
    date.setDate(0);

    while(date.getDay() > 0) {
      previousMonthDays.unshift(date.getDate());
      date.setDate(date.getDate() - 1);
    }

    return {
      dateObject: targetDate,
      month: targetDate.getMonth(),
      year: targetDate.getFullYear(),
      days: {
        previousMonth: previousMonthDays,
        thisMonth: thisMonthDays,
        nextMonth: nextMonthDays
      }
    };
  }

  isToday(year: any, month: any, date: any):boolean {
    let targetDate;

    if(this.date) {
      targetDate = this.date;
    } else if(this.targetDate === 'startDate') {
      targetDate = this.startDate;
    } else {
      targetDate = this.endDate;
    }

    let hasYear = typeof year === 'number';
    let hasMonth = typeof month === 'number';
    let hasDate = typeof date === 'number';

    if(hasYear && hasMonth && hasDate) {
      return targetDate.getDate() === date
          && targetDate.getMonth() === month
          && targetDate.getFullYear() === year;
    } else if(hasYear && hasMonth) {
      return targetDate.getMonth() === month
          && targetDate.getFullYear() === year;
    } else {
      return targetDate.getFullYear() === year;
    }
  }

  isAltToday(year: any, month: any, date: any):boolean {
    if(this.date) {
      return false;
    }

    let targetDate;

    if(this.targetDate === 'startDate') {
      targetDate = this.endDate;
    } else {
      targetDate = this.startDate;
    }

    let hasYear = typeof year === 'number';
    let hasMonth = typeof month === 'number';
    let hasDate = typeof date === 'number';

    if(hasYear && hasMonth && hasDate) {
      return targetDate.getDate() === date
          && targetDate.getMonth() === month
          && targetDate.getFullYear() === year;
    } else if(hasYear && hasMonth) {
      return targetDate.getMonth() === month
          && targetDate.getFullYear() === year;
    } else {
      return targetDate.getFullYear() === year;
    }
  }

  setTargetDateObj(year: any, month: any):void {
    let newTargetDateObj = new Date(this.targetDateObj);
    let hasYear = typeof year === 'number';
    let hasMonth = typeof month === 'number';

    this.resetDateTime(newTargetDateObj);
    if(hasYear) newTargetDateObj.setFullYear(year);
    if(hasMonth) newTargetDateObj.setMonth(month);


    if(!hasMonth) {
      this.changeCalendarView('months');
    } else {
      this.changeCalendarView('days');
    }

    this.previousMobileCalendarView();
    this.targetDateObj = newTargetDateObj;
    this.makeCalendarData();
  }

  setDate(year: any, month: any, date: any):void {
    let newDate = new Date();

    this.resetDateTime(newDate);
    newDate.setFullYear(year);
    newDate.setMonth(month);
    newDate.setDate(date);

    if(this.date) {
      this.date = newDate;
    } else if(this.targetDate === 'startDate') {
      this.startDate = newDate;
    } else {
      this.endDate = newDate;
    }
  }

  resetDateTime(date: any) {
    date.setMilliseconds(0);
    date.setSeconds(0);
    date.setMinutes(0);
    date.setHours(0);
  }

  cancel():void {
    this.hide();
  }

  done():void {
    if(this.date) {
      if(this.time) {
        let hour = parseInt(this.hours[this.hourRollerEmbla.selectedScrollSnap()]);
        let minute = parseInt(this.minutes[this.minuteRollerEmbla.selectedScrollSnap()]);
        let period = this.periods[this.periodRollerEmbla.selectedScrollSnap()];

        if(hour === 12) hour = 0;
        if(period === 'PM') hour += 12;

        this.date.setHours(hour);
        this.date.setMinutes(minute);
      }

      this.pickEventEmitter.emit({
        date: this.date
      });
    } else {
      this.pickEventEmitter.emit({
        startDate: this.startDate,
        endDate: this.endDate
      });
    }

    this.hide();
  }

  handleOutsideClick(e: any) {
    if(this.root.nativeElement === e.target) {
      this.hide();
    }
  }

  changeCalendarView(targetCalendarView: any) {
    this.calendarView = targetCalendarView;
  }

  dateIsBetweenMinAndMax(fullYear: any, month: any, day: any):boolean {
    let dateOfComparison = new Date();

    this.resetDateTime(dateOfComparison);
    dateOfComparison.setFullYear(fullYear);
    dateOfComparison.setMonth(month);
    dateOfComparison.setDate(day);

    let dateIsBetweenMinAndMax:boolean = true;

    if(this.minDate && dateOfComparison < this.minDate) {
      dateIsBetweenMinAndMax = false;
    }

    if(this.maxDate && dateOfComparison > this.maxDate) {
      dateIsBetweenMinAndMax = false;
    }

    return dateIsBetweenMinAndMax;
  }

  monthIsBetweenMinAndMax(fullYear: any, month: any):boolean {
    let minYear = (this.minDate) ? this.minDate.getFullYear() : null;
    let minMonth = (this.minDate) ? this.minDate.getMonth() : null;
    let maxYear = (this.maxDate) ? this.maxDate.getFullYear() : null;
    let maxMonth = (this.maxDate) ? this.maxDate.getMonth() : null;

    let monthIsBetweenMinAndMax = true;

    if(minYear !== null && minMonth !== null) {
      if(fullYear < minYear
      || fullYear === minYear && month < minMonth) {
        monthIsBetweenMinAndMax = false;
      }
    }

    if(maxYear !== null && maxMonth !== null) {
      if(fullYear > maxYear
      || fullYear === maxYear && month > maxMonth) {
        monthIsBetweenMinAndMax = false;
      }
    }

    return monthIsBetweenMinAndMax;
  }

  yearIsBetweenMinAndMax(fullYear: any):boolean {
    let minYear = (this.minDate) ? this.minDate.getFullYear() : null;
    let maxYear = (this.maxDate) ? this.maxDate.getFullYear() : null;

    let yearIsBetweenMinAndMax = true;

    if(minYear !== null) {
      if(fullYear < minYear) {
        yearIsBetweenMinAndMax = false;
      }
    }

    if(maxYear !== null) {
      if(fullYear > maxYear) {
        yearIsBetweenMinAndMax = false;
      }
    }

    return yearIsBetweenMinAndMax;
  }

  EmblaWheel({ holder, options } : any) {
    const embla = EmblaCarousel(
      holder,
      options
    );

    const slideNodes = embla.slideNodes();
    const totalSlides = slideNodes.length;
    const onEmblaScroll = () => {
      const currentScrollProgress = embla.scrollProgress();
      const currentProgress = currentScrollProgress * totalSlides;

      if(currentProgress < 1) {
        slideNodes[totalSlides-1].style.transform = 'translateY(' + (totalSlides * -100) + '%)';
        slideNodes[totalSlides-2].style.transform = 'translateY(' + (totalSlides * -100) + '%)';
      } else {
        slideNodes[totalSlides-1].style.transform = '';
        slideNodes[totalSlides-2].style.transform = '';
      }

      if(currentProgress > 10) {
        slideNodes[0].style.transform = 'translateY(' + (totalSlides * 100) + '%)';
        slideNodes[1].style.transform = 'translateY(' + (totalSlides * 100) + '%)';
      } else {
        slideNodes[0].style.transform = '';
        slideNodes[1].style.transform = '';
      }
    };

    embla.on('scroll', onEmblaScroll);

    onEmblaScroll();

    return embla;
  }
}
